
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ClientStatus } from "@/types/clientStatus";
import { toast } from "sonner";

export const useClientStatuses = () => {
  return useQuery({
    queryKey: ["client-statuses"],
    queryFn: async () => {
      console.log("Fetching client statuses...");
      const { data, error } = await supabase
        .from("client_statuses")
        .select("*")
        .order("sort_order", { ascending: true });
      
      if (error) {
        console.error("Error fetching client statuses:", error);
        throw error;
      }
      
      console.log("Fetched client statuses:", data);
      return data as ClientStatus[];
    },
    staleTime: 0, // Always refetch
    refetchOnWindowFocus: true,
  });
};

export const useCreateClientStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (statusData: Omit<ClientStatus, "id" | "created_at" | "updated_at">) => {
      console.log("Creating client status:", statusData);
      const { data, error } = await supabase
        .from("client_statuses")
        .insert(statusData)
        .select()
        .single();
      
      if (error) {
        console.error("Error creating client status:", error);
        throw error;
      }
      console.log("Created client status:", data);
      return data;
    },
    onSuccess: (data) => {
      console.log("Invalidating queries after create");
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ["client-statuses"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Force refetch
      queryClient.refetchQueries({ queryKey: ["client-statuses"] });
      toast.success("تم إضافة الحالة بنجاح");
    },
    onError: (error: any) => {
      console.error("Error creating client status:", error);
      toast.error("حدث خطأ أثناء إضافة الحالة");
    },
  });
};

export const useUpdateClientStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<ClientStatus> & { id: string }) => {
      console.log("Updating client status:", id, updateData);
      const { data, error } = await supabase
        .from("client_statuses")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating client status:", error);
        throw error;
      }
      console.log("Updated client status:", data);
      return data;
    },
    onSuccess: (data) => {
      console.log("Invalidating queries after update");
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ["client-statuses"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Force refetch
      queryClient.refetchQueries({ queryKey: ["client-statuses"] });
      toast.success("تم تحديث الحالة بنجاح");
    },
    onError: (error: any) => {
      console.error("Error updating client status:", error);
      toast.error("حدث خطأ أثناء تحديث الحالة");
    },
  });
};

export const useDeleteClientStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      console.log("Deleting client status:", id);
      const { error } = await supabase
        .from("client_statuses")
        .delete()
        .eq("id", id);
      
      if (error) {
        console.error("Error deleting client status:", error);
        throw error;
      }
      console.log("Deleted client status:", id);
    },
    onSuccess: () => {
      console.log("Invalidating queries after delete");
      // Invalidate and refetch immediately
      queryClient.invalidateQueries({ queryKey: ["client-statuses"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Force refetch
      queryClient.refetchQueries({ queryKey: ["client-statuses"] });
      toast.success("تم حذف الحالة بنجاح");
    },
    onError: (error: any) => {
      console.error("Error deleting client status:", error);
      toast.error("حدث خطأ أثناء حذف الحالة");
    },
  });
};
